import { formatMoney } from '@scale/currency';
import { DialogBase } from '@scale/dialog-base';

class ScaleUgcGallery extends DialogBase {
  #items = [];
  #currentIndex = 0;
  #pointerStartX = 0;
  #swiping = false;

  setupDialog() {
    this.#items = [...this.ref('grid').querySelectorAll('.block-ugc-item')];

    this.ref('dialog').addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.prev();
      else if (e.key === 'ArrowRight') this.next();
    }, { signal: this.signal });

    const media = this.ref('media');
    media.addEventListener('pointerdown', (e) => this.#onPointerDown(e), { signal: this.signal });
    media.addEventListener('pointermove', (e) => this.#onPointerMove(e), { signal: this.signal });
    media.addEventListener('pointerup', (e) => this.#onPointerUp(e), { signal: this.signal });
    media.addEventListener('pointercancel', () => this.#resetSwipe(), { signal: this.signal });
  }

  openItem(e) {
    const btn = e.target.closest('.block-ugc-item');
    if (!btn) return;
    const index = this.#items.indexOf(btn);
    if (index === -1) return;
    this.#currentIndex = index;
    this.#render();
    this.open();
  }

  prev() {
    if (this.#currentIndex <= 0) return;
    this.#currentIndex--;
    this.#render();
  }

  next() {
    if (this.#currentIndex >= this.#items.length - 1) return;
    this.#currentIndex++;
    this.#render();
  }

  #render() {
    const item = this.#items[this.#currentIndex];
    const media = this.ref('media');
    const details = this.ref('details');

    media.replaceChildren();
    details.replaceChildren();

    const videoType = item.dataset.videoType;
    if (videoType === 'shopify') {
      const video = document.createElement('video');
      video.src = item.dataset.videoUrl;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.muted = true;
      media.appendChild(video);
    } else if (videoType === 'youtube') {
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${item.dataset.videoId}?autoplay=1`;
      iframe.allow = 'autoplay; encrypted-media';
      iframe.allowFullscreen = true;
      iframe.loading = 'lazy';
      media.appendChild(iframe);
    } else if (videoType === 'vimeo') {
      const iframe = document.createElement('iframe');
      iframe.src = `https://player.vimeo.com/video/${item.dataset.videoId}?autoplay=1`;
      iframe.allow = 'autoplay; fullscreen';
      iframe.allowFullscreen = true;
      iframe.loading = 'lazy';
      media.appendChild(iframe);
    } else if (item.dataset.imageUrl) {
      const img = document.createElement('img');
      img.src = item.dataset.imageUrl;
      img.alt = '';
      img.width = item.dataset.imageWidth;
      img.height = item.dataset.imageHeight;
      img.loading = 'eager';
      media.appendChild(img);
    }

    const rating = Number(item.dataset.rating || 0);
    if (rating > 0) {
      const stars = this.#createEl('div', 'block-ugc-gallery__lightbox-stars');
      stars.setAttribute('aria-label', `${rating} out of 5 stars`);
      for (let i = 0; i < 5; i++) {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', '16');
        svg.setAttribute('height', '16');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('fill', i < rating ? 'currentColor' : 'none');
        svg.setAttribute('stroke', 'currentColor');
        svg.setAttribute('stroke-width', '2');
        const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        polygon.setAttribute('points', '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2');
        svg.appendChild(polygon);
        stars.appendChild(svg);
      }
      details.appendChild(stars);
    }

    if (item.dataset.author) {
      const authorEl = this.#createEl('div', 'block-ugc-gallery__lightbox-author');
      authorEl.textContent = item.dataset.author;
      if (item.dataset.date) {
        const dateEl = this.#createEl('span', 'block-ugc-gallery__lightbox-date');
        dateEl.textContent = ` · ${item.dataset.date}`;
        authorEl.appendChild(dateEl);
      }
      details.appendChild(authorEl);
    }

    if (item.dataset.text) {
      const textEl = this.#createEl('div', 'block-ugc-gallery__lightbox-text');
      textEl.textContent = item.dataset.text;
      details.appendChild(textEl);
    }

    const productUrl = item.dataset.productUrl;
    if (item.dataset.productTitle && productUrl?.startsWith('/')) {
      const link = document.createElement('a');
      link.href = productUrl;
      link.className = 'block-ugc-gallery__lightbox-product';

      if (item.dataset.productImage) {
        const pImg = document.createElement('img');
        pImg.src = item.dataset.productImage;
        pImg.alt = '';
        pImg.className = 'block-ugc-gallery__lightbox-product-img';
        pImg.width = 48;
        pImg.height = 48;
        pImg.loading = 'lazy';
        link.appendChild(pImg);
      }

      const info = document.createElement('div');
      const title = this.#createEl('div', 'block-ugc-gallery__lightbox-product-title');
      title.textContent = item.dataset.productTitle;
      info.appendChild(title);
      const price = this.#createEl('div', 'block-ugc-gallery__lightbox-product-price');
      price.textContent = formatMoney(Number(item.dataset.productPrice));
      info.appendChild(price);
      link.appendChild(info);
      details.appendChild(link);
    }

    this.ref('prev').disabled = this.#currentIndex === 0;
    this.ref('next').disabled = this.#currentIndex >= this.#items.length - 1;
  }

  #createEl(tag, className) {
    const el = document.createElement(tag);
    el.className = className;
    return el;
  }

  #onPointerDown(e) {
    if (e.pointerType === 'mouse') return;
    this.#pointerStartX = e.clientX;
    this.#swiping = true;
  }

  #onPointerMove(e) {
    if (!this.#swiping) return;
    const delta = Math.abs(e.clientX - this.#pointerStartX);
    if (delta > 10) e.preventDefault();
  }

  #onPointerUp(e) {
    if (!this.#swiping) return;
    this.#swiping = false;
    const delta = e.clientX - this.#pointerStartX;
    if (delta > 50) this.prev();
    else if (delta < -50) this.next();
  }

  #resetSwipe() {
    this.#swiping = false;
  }

  onBlockSelect(e) {
    const blockEl = this.querySelector(`[id*="${e.detail.blockId}"] .block-ugc-item, .block-ugc-item[id*="${e.detail.blockId}"]`);
    if (blockEl) {
      const index = this.#items.indexOf(blockEl);
      if (index !== -1) {
        this.#currentIndex = index;
        this.#render();
        this.open();
        return;
      }
    }
    super.onBlockSelect(e);
  }

  onBlockDeselect() {
    this.close();
  }
}

if (!customElements.get('scale-ugc-gallery')) {
  customElements.define('scale-ugc-gallery', ScaleUgcGallery);
}
